import React, { useMemo } from 'react';
import { createPortal } from 'react-dom';

export interface UsePortalOptions {
  /** When true, children render via createPortal. @default false */
  enabled?: boolean;
  /** Portal target element. @default document.body */
  container?: Element | null;
}

export interface UsePortalResult {
  /** Wrap children in this component — renders inline or via portal based on `enabled`. */
  PortalWrapper: React.FC<{ children: React.ReactNode }>;
}

function InlinePassthrough({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}

/**
 * Conditionally render children into a portal target.
 *
 * When `enabled` is false (default) or during SSR, children render inline.
 * When `enabled` is true on the client, children render via `createPortal`
 * to the specified container (defaults to `document.body`).
 */
export function usePortal(options?: UsePortalOptions): UsePortalResult {
  const { enabled = false, container = null } = options ?? {};

  const PortalWrapper = useMemo(() => {
    if (!enabled || typeof document === 'undefined') {
      return InlinePassthrough;
    }

    const target = container ?? document.body;

    function Portal({ children }: { children: React.ReactNode }) {
      return createPortal(children, target);
    }

    return Portal;
  }, [enabled, container]);

  return { PortalWrapper };
}
